import type { LngLat } from "./types.ts";

export interface ScreenPoint {
  x: number;
  y: number;
}

export type LassoMode = "replace" | "add" | "subtract";

export interface EditableGraph {
  points: LngLat[];
  segments: Array<[number, number]>;
  start: number | null;
  selection: number[];
}

/** Even-odd test. A point on the outline counts as inside. */
export function pointInRing(point: ScreenPoint, ring: readonly ScreenPoint[]): boolean {
  if (ring.length < 3) return false;
  if (onRingBoundary(point, ring)) return true;
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const a = ring[i];
    const b = ring[j];
    if (!a || !b || a.y === b.y) continue;
    const crosses = (a.y > point.y) !== (b.y > point.y);
    if (!crosses) continue;
    const x = ((b.x - a.x) * (point.y - a.y)) / (b.y - a.y) + a.x;
    if (point.x < x) inside = !inside;
  }
  return inside;
}

export function indicesInside(points: readonly ScreenPoint[], ring: readonly ScreenPoint[]): number[] {
  const inside: number[] = [];
  for (let i = 0; i < points.length; i++) {
    const point = points[i];
    if (point && pointInRing(point, ring)) inside.push(i);
  }
  return inside;
}

/** A segment counts when an endpoint is inside the ring or the segment crosses an edge. */
export function segmentsHittingRing(
  points: readonly ScreenPoint[],
  segments: ReadonlyArray<readonly [number, number]>,
  ring: readonly ScreenPoint[],
): number[] {
  if (ring.length < 3) return [];
  const found: number[] = [];
  for (let index = 0; index < segments.length; index++) {
    const pair = segments[index];
    if (!pair) continue;
    const a = points[pair[0]];
    const b = points[pair[1]];
    if (!a || !b) continue;
    if (pointInRing(a, ring) || pointInRing(b, ring) || crossesRing(a, b, ring)) found.push(index);
  }
  return found;
}

export function applyLasso(current: readonly number[], inside: readonly number[], mode: LassoMode): number[] {
  if (mode === "replace") return uniqueSorted(inside);
  if (mode === "add") return uniqueSorted([...current, ...inside]);
  const drop = new Set(inside);
  return current.filter((index) => !drop.has(index));
}

/** Lines with both ends in the selection. Derived, not stored. */
export function fullySelectedSegments(
  segments: ReadonlyArray<readonly [number, number]>,
  selection: readonly number[],
): number[] {
  const picked = new Set(selection);
  const found: number[] = [];
  segments.forEach(([a, b], index) => {
    if (picked.has(a) && picked.has(b)) found.push(index);
  });
  return found;
}

/**
 * Drop the given points and every line that touched one.
 * The input graph is left unchanged so one saved copy is the undo step.
 */
export function deleteSelection(
  graph: EditableGraph,
  remove: readonly number[],
): EditableGraph & { pointMap: Int32Array } {
  const drop = new Set(remove.filter((index) => index >= 0 && index < graph.points.length));
  const map = new Array<number>(graph.points.length).fill(-1);
  const points: LngLat[] = [];
  for (let i = 0; i < graph.points.length; i++) {
    if (drop.has(i)) continue;
    const at = graph.points[i];
    if (!at) continue;
    map[i] = points.length;
    points.push([at[0], at[1]]);
  }
  const segments: Array<[number, number]> = [];
  const seen = new Set<string>();
  for (const [a, b] of graph.segments) {
    if (drop.has(a) || drop.has(b)) continue;
    const na = map[a] ?? -1;
    const nb = map[b] ?? -1;
    if (na < 0 || nb < 0 || na === nb) continue;
    const lo = Math.min(na, nb);
    const hi = Math.max(na, nb);
    const key = `${lo}:${hi}`;
    if (seen.has(key)) continue;
    seen.add(key);
    segments.push([lo, hi]);
  }
  const mappedStart = graph.start == null ? -1 : (map[graph.start] ?? -1);
  const start = mappedStart >= 0 ? mappedStart : null;
  const selection = graph.selection.flatMap((index) => {
    const next = map[index] ?? -1;
    return next >= 0 ? [next] : [];
  });
  return { points, segments, start, selection: uniqueSorted(selection), pointMap: Int32Array.from(map) };
}

/** Points that survived an erase stay in order, so selection indexes can follow them. */
export function remapSelection(
  before: readonly LngLat[],
  after: readonly LngLat[],
  selection: readonly number[],
): number[] {
  let next = 0;
  const map = new Array<number>(before.length).fill(-1);
  for (let i = 0; i < before.length && next < after.length; i++) {
    const old = before[i];
    const kept = after[next];
    if (!old || !kept) continue;
    if (old[0] === kept[0] && old[1] === kept[1]) {
      map[i] = next;
      next += 1;
    }
  }
  return selection.flatMap((index) => {
    const mapped = map[index] ?? -1;
    return mapped >= 0 ? [mapped] : [];
  });
}

function uniqueSorted(indices: readonly number[]): number[] {
  return [...new Set(indices)].sort((a, b) => a - b);
}

function crossesRing(a: ScreenPoint, b: ScreenPoint, ring: readonly ScreenPoint[]): boolean {
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const c = ring[j];
    const d = ring[i];
    if (c && d && properCross(a, b, c, d)) return true;
  }
  return false;
}

function properCross(a: ScreenPoint, b: ScreenPoint, c: ScreenPoint, d: ScreenPoint): boolean {
  return orient(a, b, c) !== orient(a, b, d) && orient(c, d, a) !== orient(c, d, b);
}

function orient(a: ScreenPoint, b: ScreenPoint, c: ScreenPoint): number {
  const value = (b.x - a.x) * (c.y - a.y) - (b.y - a.y) * (c.x - a.x);
  if (value > 0) return 1;
  if (value < 0) return -1;
  return 0;
}

function onRingBoundary(point: ScreenPoint, ring: readonly ScreenPoint[]): boolean {
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const a = ring[j];
    const b = ring[i];
    if (!a || !b) continue;
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const len2 = dx * dx + dy * dy;
    const t = len2 === 0 ? 0 : Math.max(0, Math.min(1, ((point.x - a.x) * dx + (point.y - a.y) * dy) / len2));
    const dist = Math.hypot(point.x - (a.x + t * dx), point.y - (a.y + t * dy));
    if (dist <= 0.5) return true;
  }
  return false;
}
